import { useNavigate } from 'react-router-dom'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { ApplicationsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  AtsApplicationCreateFromCandidatesRecommendationsDocument,
  AtsJobPostingApplicationsPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { JobMultiPostErrors } from 'modules/ats/components/JobMultiPostErrors'
import { JobPostingLayout } from 'modules/ats/components/job_postings/JobPostingLayout'
import { useJobDetailsContext } from 'modules/ats/pages/JobsPage/JobDetails'

import ApplicationsTable from './components/ApplicationsTable'

const JobApplicationsPage = () => {
  const { job } = useJobDetailsContext()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { access, company } = useCurrentSession()

  const { data } = useQuery(AtsJobPostingApplicationsPageDocument, {
    variables: { id: job.id, accessId: access.id, companyId: company.id },
  })

  const [addAISuggestions] = useMutation(
    AtsApplicationCreateFromCandidatesRecommendationsDocument,
    {
      extractorFn: (mutation_data) =>
        mutation_data.atsMutations.createFromCandidatesRecommendationsAtsApplication,
      refetchQueries: [ApplicationsDataTableDocument],
    }
  )

  const jobData = data?.ats.jobPosting
  const aiSearchEnabled = data?.features.featuresConnection.nodes.find(
    (node) => node.name === 'dev_ats_ai_search'
  )?.enabled
  if (!jobData) {
    return (
      <Box padding="s40" width="full" alignItems="center" flexGrow>
        <Spinner />
      </Box>
    )
  }

  const canManageJobPostings = data?.ats._policies.manageJobPostings.can ?? false
  const isReviewerRole =
    jobData?.currentHiringRole[0]?.hiringRole === 'reviewer' && !canManageJobPostings

  const allowsNewCandidates =
    !isReviewerRole &&
    (jobData?.status === 'draft' ||
      jobData?.status === 'published' ||
      jobData?.status === 'unlisted')

  const onAddAISuggestions = async () => {
    const response = await addAISuggestions({
      variables: { jobPostingId: jobData.id },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      const applications = result.data.applications ?? []
      if (applications.length > 0) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.job_details.add_candidate.add_ai_suggestions_success'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ats.job_details.add_candidate.add_ai_suggestions_no_candidates'),
        })
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.job_details.add_candidate.add_ai_suggestions_error'),
      })
    }
  }

  return (
    <JobPostingLayout job={job} isReviewerRole={isReviewerRole}>
      <Box width="full">
        <Box gap="s16">
          <JobMultiPostErrors
            atsJobPostingId={jobData.id}
            locationId={jobData.location?.id}
            removable
          />

          {allowsNewCandidates && (
            <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
              {aiSearchEnabled && (
                <Button
                  type="positive"
                  iconName="ai"
                  aria-label={i18n.t('ats.job_details.add_candidate.add_ai_suggestions')}
                  onClick={() => onAddAISuggestions()}
                >
                  {i18n.t('ats.job_details.add_candidate.add_ai_suggestions')}
                </Button>
              )}
              <Button.Split
                iconName="add"
                label={i18n.t('ats.job_details.add_candidate.add_candidate')}
                type="primary"
                dropdownAriaLabel="more options"
                onClick={() =>
                  navigate(
                    navigation.ats.jobs.jobsId.applications.new.fullPath({ jobId: jobData.id })
                  )
                }
              >
                <Button.Split.Option
                  iconName="pencil"
                  description={i18n.t(
                    'ats.job_details.add_candidate.add_internal_candidate_description'
                  )}
                  onClick={() =>
                    navigate(
                      navigation.ats.jobs.jobsId.applications.newEmployeeCandidate.fullPath({
                        jobId: jobData.id,
                      })
                    )
                  }
                >
                  {i18n.t('ats.job_details.add_candidate.add_internal_candidate')}
                </Button.Split.Option>
                <Button.Split.Option
                  iconName="file-filled"
                  description={i18n.t('ats.job_details.add_candidate.upload_cvs_description')}
                  onClick={() =>
                    navigate(
                      navigation.ats.jobs.jobsId.applications.newFromCv.fullPath({
                        jobId: jobData.id,
                      })
                    )
                  }
                >
                  {i18n.t('ats.job_details.add_candidate.upload_cvs')}
                </Button.Split.Option>
              </Button.Split>
            </Box>
          )}
        </Box>
        {/* TO DO: we should pass the job data to the ApplicationsTable */}
        <ApplicationsTable jobId={jobData.id} />
      </Box>
    </JobPostingLayout>
  )
}

export default JobApplicationsPage
